import { useState } from 'react'

import { ActionIcon, Loader, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'

import IconAdd from 'src/icons/IconAdd'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconChevronLeft from 'src/icons/IconChevronLeft'
import { cn } from 'src/utils'

import type {
  AgendaGroup,
  AgendaGroupKey,
  AgendaItem,
} from '../lib/agendaItems'
import {
  findNowMarkerIndex,
  isSameDay,
  OVERDUE_COLLAPSE_THRESHOLD,
} from '../lib/agendaItems'

import AgendaRow from './AgendaRow'
import WidgetShell from './WidgetShell'

/**
 * The agenda widget's presentation. Takes already-merged groups, so it needs no
 * Apollo, router or context providers — which is what lets Storybook cover every
 * visual state directly.
 */
export interface AgendaWidgetViewProps {
  groups: AgendaGroup[]
  /** Drives the now marker. Injected so stories and tests are deterministic. */
  now?: Date
  loading?: boolean
  onAddTask?: () => void
  onOpenApplicant?: (applicantId: string, options: { newTab: boolean }) => void
  onAction?: (item: AgendaItem) => void
  onCompleteTask?: (item: AgendaItem) => void | Promise<unknown>
  /** Opens the task itself, so a task row is not dead space. */
  onOpenTask?: (item: AgendaItem) => void
  /** The day being shown. Defaults to today. */
  selectedDate?: Date
  /** Steps the day by `delta` days. */
  onChangeDay?: (delta: number) => void
  /** Jumps back to today. */
  onGoToToday?: () => void
  /**
   * Names each row's owner. On whenever the page scope covers more than one
   * person — see `scopeShowsOwner`.
   */
  showOwner?: boolean
  /**
   * The signed-in employee. Rows they own keep live icons; everyone else's go
   * quiet, because the phone dials from this device and the tick completes
   * somebody else's task.
   */
  currentEmployeeId?: string | null
  /**
   * How many of today's items the page limits are holding back. Rendered as a
   * quiet footer so a truncated company-wide day says so.
   */
  hiddenCount?: number
}

const GroupHeader = ({
  group,
  collapsed,
  onToggle,
}: {
  group: AgendaGroup
  collapsed: boolean
  onToggle: () => void
}) => (
  // The label is identical expanded or collapsed — only the chevron rotates.
  // Collapsing must not change what the header says.
  //
  // Sticky so a long list never leaves you without a heading: scrolling deep
  // into carryover otherwise loses which group you are in. The background must
  // stay fully opaque or rows show through as they pass underneath.
  <button
    type="button"
    aria-expanded={!collapsed}
    onClick={onToggle}
    className={cn(
      'sticky top-0 z-10 mt-1 flex w-full flex-row items-center gap-1.5 rounded px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-wide',
      group.key === 'OVERDUE'
        ? 'bg-doubleNickel-error-50 text-doubleNickel-error-700'
        : 'bg-doubleNickel-gray-100 text-doubleNickel-gray-600'
    )}
  >
    <IconChevronDown
      className={cn(
        'h-3 w-3 flex-shrink-0 fill-none stroke-current transition-transform',
        collapsed && '-rotate-90'
      )}
    />
    {group.label} ({group.items.length})
  </button>
)

/**
 * The red line marking where we are in the day. Rendered wherever "now" falls,
 * including past the last row — a line that vanishes at the end of the day is
 * exactly when you most want to see that everything is behind you.
 */
const NowMarker = () => (
  <div
    data-testid="agenda-now-marker"
    className="flex flex-row items-center gap-1.5 px-2 py-1"
  >
    <span className="text-[10px] font-semibold tracking-wide text-doubleNickel-error-600">
      NOW
    </span>
    <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-doubleNickel-error-500" />
    <span className="h-px flex-1 bg-doubleNickel-error-400" />
  </div>
)

/**
 * Sits under the now line once nothing is left ahead. Without it, a day whose
 * work is all behind you looks identical to one still loading — the list just
 * stops, and the eye keeps looking for the next row.
 */
const EndOfDayMarker = () => (
  <div
    data-testid="agenda-end-of-day"
    className="px-2 pb-2 pt-1 text-center text-[11px] text-doubleNickel-gray-400"
  >
    Nothing further today
  </div>
)

const AgendaWidgetView = ({
  groups,
  now = new Date(),
  loading = false,
  onAddTask,
  onOpenApplicant,
  onAction,
  onCompleteTask,
  onOpenTask,
  selectedDate,
  onChangeDay,
  onGoToToday,
  showOwner = false,
  currentEmployeeId,
  hiddenCount = 0,
}: AgendaWidgetViewProps) => {
  // Only user toggles are stored. The default is derived from the group's size
  // on every render, so a group that arrives after the first paint (data loads
  // async) still gets the right initial state without an effect.
  const [toggled, setToggled] = useState<
    Partial<Record<AgendaGroupKey, boolean>>
  >({})

  const day = selectedDate ?? now
  const viewingToday = isSameDay(day, now)

  const isCollapsed = (group: AgendaGroup) =>
    toggled[group.key] ??
    ((group.key === 'OVERDUE' &&
      group.items.length > OVERDUE_COLLAPSE_THRESHOLD) ||
      // Earlier work is behind you by definition, so it starts folded whenever
      // the domain layer decided there was enough of it to warrant a group.
      group.key === 'EARLIER')

  // With an `Earlier today` group present the split already sits exactly where
  // the marker would have gone, so drawing it too would state the boundary
  // twice.
  const hasEarlierGroup = groups.some((group) => group.key === 'EARLIER')

  // A folded day with nothing left ahead has no `Still to come` group for the
  // end-of-day line to sit inside, so it goes after the groups instead. Without
  // this the day ends on a collapsed header and simply stops.
  const foldedDayIsDone =
    viewingToday &&
    hasEarlierGroup &&
    !groups.some((group) => group.key === 'TODAY')

  // Nothing to act on when the row is not yours. Under a solo scope every row
  // is, so the check collapses to `true`.
  //
  // Matches against every owner rather than `owner.employeeId`: a task can have
  // several assignees, and `owner` only names the first, so matching on it would
  // strip a co-assignee of the actions on a task that is genuinely theirs.
  const canActOn = (item: AgendaItem) =>
    !showOwner ||
    (Boolean(currentEmployeeId) &&
      item.ownerEmployeeIds.includes(currentEmployeeId as string))

  const allItems = groups.flatMap((group) => group.items)

  // Hovering a chevron names the day it would take you to, so stepping is not a
  // guess. Shown as "Tue, Jul 28" — the same shape as the header's own date.
  const adjacentDayLabel = (delta: number) =>
    dayjs(day).add(delta, 'day').format('ddd, MMM D')

  return (
    <WidgetShell
      title={
        /* The weekday names the day rather than "Today" — the date beside it
           removes any ambiguity, and the Today shortcut on the right is what
           tells you when you have navigated away. */
        <div className="flex min-w-0 flex-row items-baseline gap-2">
          <span className="min-w-0 truncate text-base font-medium text-doubleNickel-gray-800">
            {dayjs(day).format('dddd')}
          </span>
          <span className="flex-shrink-0 text-[11px] font-medium text-doubleNickel-gray-500">
            {dayjs(day).format('MMM D')}
          </span>
        </div>
      }
      controls={
        <div className="flex flex-shrink-0 flex-row items-center gap-0.5">
          <Tooltip label={adjacentDayLabel(-1)} withArrow position="top">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label={`Previous day, ${adjacentDayLabel(-1)}`}
              onClick={() => onChangeDay?.(-1)}
            >
              <IconChevronLeft className="h-3 w-3 fill-none stroke-doubleNickel-gray-600" />
            </ActionIcon>
          </Tooltip>
          {/* Only offered when it would do something, so it doubles as a hint
              that you have navigated away from today. */}
          {!viewingToday && (
            <button
              type="button"
              onClick={() => onGoToToday?.()}
              className="rounded px-1.5 py-0.5 text-[11px] font-medium text-doubleNickel-brand-600 hover:bg-doubleNickel-brand-25"
            >
              Today
            </button>
          )}
          <Tooltip label={adjacentDayLabel(1)} withArrow position="top">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label={`Next day, ${adjacentDayLabel(1)}`}
              onClick={() => onChangeDay?.(1)}
            >
              <IconChevronLeft className="h-3 w-3 rotate-180 fill-none stroke-doubleNickel-gray-600" />
            </ActionIcon>
          </Tooltip>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="Add a task"
            onClick={() => onAddTask?.()}
          >
            <IconAdd className="h-4 w-4 fill-none stroke-doubleNickel-gray-800" />
          </ActionIcon>
        </div>
      }
    >
      <>
        {loading ? (
          <Loader
            className="flex h-full w-full items-center justify-center"
            size="xs"
            color="gray"
          />
        ) : allItems.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-doubleNickel-gray-500">
            {viewingToday
              ? 'Nothing on the clock today'
              : `Nothing on the clock ${dayjs(day).format('ddd, MMM D')}`}
          </div>
        ) : (
          groups.map((group) => {
            const collapsed = isCollapsed(group)
            // The marker only belongs on today's clock; overdue work has no
            // honest position relative to "now".
            // Neither marker means anything on a day that is not today.
            const nowIndex =
              viewingToday &&
              !hasEarlierGroup &&
              group.key === 'TODAY' &&
              group.items.length > 0
                ? findNowMarkerIndex(group.items, now)
                : -1

            return (
              <div key={group.key} className="flex flex-col">
                <GroupHeader
                  group={group}
                  collapsed={collapsed}
                  onToggle={() =>
                    setToggled((prev) => ({ ...prev, [group.key]: !collapsed }))
                  }
                />
                {/* Unmounted rather than hidden while collapsed: a long
                    carryover list shouldn't stay in the tab order or be
                    reachable by search when the user has folded it away. */}
                {!collapsed && (
                  <div className="flex flex-col">
                    {group.items.map((item, index) => {
                      // Compared against the rendered string rather than the
                      // timestamp: two bookings 20 seconds apart both read
                      // "9:40 AM", and it is the printed time that repeats.
                      const previous = group.items[index - 1]
                      const hideTime =
                        Boolean(previous) &&
                        dayjs(previous.at).format('h:mm A') ===
                          dayjs(item.at).format('h:mm A')
                      return (
                        <div key={item.id} className="flex flex-col">
                          {index === nowIndex && <NowMarker />}
                          <AgendaRow
                            item={item}
                            onOpenApplicant={onOpenApplicant}
                            onAction={onAction}
                            onComplete={onCompleteTask}
                            onOpenTask={onOpenTask}
                            showOwner={showOwner}
                            hideTime={hideTime}
                            canAct={canActOn(item)}
                          />
                        </div>
                      )
                    })}
                    {/* Everything is behind us: the now line belongs after the
                        last row, and the wording sits under it. Previously the
                        two were mutually exclusive — `index === nowIndex` can
                        never match once nowIndex is past the last index — so
                        the line disappeared exactly when the day was done. */}
                    {nowIndex === group.items.length && (
                      <>
                        <NowMarker />
                        <EndOfDayMarker />
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })
        )}
        {foldedDayIsDone && <EndOfDayMarker />}
        {/* Grey rather than amber on purpose: a big account's company-wide day
            is always truncated, so a warning colour here would be permanent
            furniture — the same failure as the all-red overdue pills. */}
        {hiddenCount > 0 && (
          <div
            data-testid="agenda-truncated"
            className="px-2 py-2 text-center text-[11px] text-doubleNickel-gray-500"
          >
            {hiddenCount} more not shown
          </div>
        )}
      </>
    </WidgetShell>
  )
}

export default AgendaWidgetView
